import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Cookie } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

const COOKIE_CONSENT_KEY = 'monkey_cookie_consent';

export function CookieBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Don't show cookie banner on native mobile apps
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setIsVisible(false);
  };

  const handleClose = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-4xl mx-auto bg-surface rounded-xl shadow-2xl border border-hairline">
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-surface-3 rounded-full flex items-center justify-center">
              <Cookie className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-sans text-lg font-semibold text-ink mb-2">
                {t('cookies.title', 'Uso de Cookies')}
              </h3>
              <p className="text-sm text-ink-muted mb-3">
                {t('cookies.message', 'Utilizamos cookies estrictamente necesarias para mantener el correcto funcionamiento de la aplicación. Si continúas navegando, aceptas nuestra política de cookies.')}
              </p>
              <div className="flex flex-wrap gap-2 text-sm">
                <Link
                  to="/cookies-policy"
                  className="text-ink-muted underline hover:text-ink"
                >
                  {t('cookies.cookiesPolicy', 'Política de Cookies')}
                </Link>
                <span className="text-ink-subtle">•</span>
                <Link
                  to="/privacy-policy"
                  className="text-ink-muted underline hover:text-ink"
                >
                  {t('cookies.privacyPolicy', 'Política de Privacidad')}
                </Link>
                <span className="text-ink-subtle">•</span>
                <Link
                  to="/terms-of-service"
                  className="text-ink-muted underline hover:text-ink"
                >
                  {t('cookies.termsOfService', 'Términos de Servicio')}
                </Link>
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={handleAccept}
                className="px-4 py-2 bg-accent hover:bg-accent-hover text-on-accent font-medium rounded-lg transition-colors text-sm"
              >
                {t('cookies.accept', 'Aceptar')}
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-ink-subtle hover:text-ink transition-colors"
                aria-label={t('common.close', 'Cerrar')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;
